import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Users, Plus, FileText, Activity, MapPin, AlertTriangle, Loader2, ChevronRight, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import MapView from '@/components/MapView';
import { getLoadStatusClasses } from '@/lib/statusColors';

const milesToKm = (miles) => Math.round((miles || 0) * 1.60934);

export default function AdminDrivers() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [drivers, setDrivers] = useState([]);
  const [loads, setLoads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [driverTab, setDriverTab] = useState('active');
  const [expiringDocs, setExpiringDocs] = useState(0);

  const fetchData = () => {
        Promise.all([
      api.get('/drivers'),
      api.get('/loads'),
      api.get('/documents/expiring?days=30').catch(() => ({ data: [] })),
    ]).then(([dr, lr, docs]) => {
      setDrivers(dr.data || []);
      setLoads(lr.data || []);
      setExpiringDocs(docs.data?.length || 0);
    }).catch(console.error).finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, [token]);

  const getDriverStats = (d) => {
    const driverLoads = loads.filter(l => l.driver_id === d.user_id);
    const completed = driverLoads.filter(l => ['completed', 'invoiced'].includes(l.status));
    const active = driverLoads.filter(l => ['assigned', 'at_pickup', 'in_transit', 'at_delivery'].includes(l.status));
    const totalKm = completed.reduce((s, l) => s + milesToKm(l.distance_miles), 0);
    const earnings = completed.reduce((s, l) => s + (l.rate || 0), 0);
    return { completed: completed.length, active: active.length, totalKm, earnings, activeLoads: active };
  };

  // Active = approved drivers WITH active loads
  const activeDrivers = drivers.filter(d => d.onboarding_completed && (getDriverStats(d).active > 0));
  // Onboarding = not yet approved
  const onboardingDrivers = drivers.filter(d => !d.onboarding_completed);
  // Inactive = approved but NO active loads right now
  const inactiveDrivers = drivers.filter(d => d.onboarding_completed && getDriverStats(d).active === 0);
  const currentList = driverTab === 'active' ? activeDrivers : driverTab === 'onboarding' ? onboardingDrivers : inactiveDrivers;

  const activeLoadCount = loads.filter(l => ['assigned', 'at_pickup', 'in_transit', 'at_delivery'].includes(l.status)).length;
  const totalKmAll = loads.filter(l => ['completed', 'invoiced'].includes(l.status)).reduce((s, l) => s + milesToKm(l.distance_miles), 0);

  const mapMarkers = drivers.filter(d => d.last_location?.lat).map(d => ({
    id: d.user_id, lat: d.last_location.lat, lng: d.last_location.lng,
    label: d.name, type: 'truck', color: '#E5A100',
    detailId: d.user_id, detailType: 'driver',
    driverInfo: { phone: d.phone, truck_type: d.truck_type, status: d.onboarding_completed ? 'Active' : 'Onboarding' },
  }));

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 text-muted-foreground animate-spin" /></div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Drivers</h1>
          <p className="text-muted-foreground text-sm">{drivers.length} drivers total</p>
        </div>
        <CreateDriverDialog open={createOpen} onOpenChange={setCreateOpen} token={token} onCreated={fetchData} />
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {[
          { icon: Users, label: 'Total Drivers', value: drivers.length, accent: 'text-brand-accent' },
          { icon: FileText, label: 'Expiring Docs', value: expiringDocs, accent: 'text-amber-400' },
          { icon: Activity, label: 'Active Loads', value: activeLoadCount, accent: 'text-foreground' },
          { icon: MapPin, label: 'Total KM', value: totalKmAll.toLocaleString(), accent: 'text-emerald-400' },
        ].map((s, i) => (
          <Card key={i} className="bg-card border-border"><CardContent className="p-4">
            <s.icon className={`w-4 h-4 ${s.accent} mb-2`} strokeWidth={1.5} />
            <p className="text-xl font-bold text-foreground">{s.value}</p>
            <p className="text-[10px] text-muted-foreground mt-1">{s.label}</p>
          </CardContent></Card>
        ))}
      </div>

      {/* Map */}
      <Card className="bg-card border-border mb-6">
        <CardContent className="p-0 px-4 py-4">
          <div className="h-[280px] rounded-xl overflow-hidden border border-border">
            <MapView markers={mapMarkers} dark={false} zoom={4} showLegend showSatelliteToggle
              onViewDetails={(type, id) => navigate(`/admin/drivers/${id}`)} />
          </div>
        </CardContent>
      </Card>

      {/* Driver Tabs: Active / Onboarding / Inactive */}
      <div className="flex items-center gap-2 mb-4">
        {[
          { id: 'active', label: 'Active', count: activeDrivers.length },
          { id: 'onboarding', label: 'Onboarding', count: onboardingDrivers.length },
          { id: 'inactive', label: 'Inactive', count: inactiveDrivers.length },
        ].map(t => (
          <button key={t.id} onClick={() => setDriverTab(t.id)}
            className={`text-[10px] tracking-wider font-bold px-4 py-2 rounded-lg transition-colors ${
              driverTab === t.id ? 'bg-brand-accent/10 text-brand-accent border border-brand-accent/20' : 'text-muted-foreground hover:text-foreground border border-transparent'
            }`}>
            {t.label.toUpperCase()} <span className="ml-1.5 bg-muted px-1.5 py-0.5 rounded text-[10px]">{t.count}</span>
          </button>
        ))}
      </div>

      {/* Drivers Table */}
      <Card className="bg-card border-border">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-muted-foreground text-[10px] tracking-wide">DRIVER</TableHead>
                <TableHead className="text-muted-foreground text-[10px] tracking-wide">STATUS</TableHead>
                <TableHead className="text-muted-foreground text-[10px] tracking-wide">ACTIVE LOADS</TableHead>
                <TableHead className="text-muted-foreground text-[10px] tracking-wide">COMPLETED</TableHead>
                <TableHead className="text-muted-foreground text-[10px] tracking-wide">TOTAL KM</TableHead>
                <TableHead className="text-muted-foreground text-[10px] tracking-wide">EARNINGS</TableHead>
                <TableHead className="text-muted-foreground text-[10px] tracking-wide"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {currentList.map(drv => {
                const stats = getDriverStats(drv);
                return (
                  <TableRow key={drv.user_id} className="border-border hover:bg-muted/30 cursor-pointer"
                    onClick={() => navigate(driverTab === 'onboarding' ? `/admin/drivers/${drv.user_id}/onboarding` : `/admin/drivers/${drv.user_id}`)}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-brand-accent/20 flex items-center justify-center text-xs font-bold text-brand-accent">{drv.name?.charAt(0)}</div>
                        <div><p className="text-xs text-foreground font-medium">{drv.name}</p><p className="text-[10px] text-muted-foreground">{drv.email}</p></div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {driverTab === 'onboarding' ? (
                        <Badge className="text-[10px] rounded-sm bg-amber-500/20 text-amber-400">PENDING REVIEW</Badge>
                      ) : (
                        <Badge className={`text-[10px] rounded-sm ${stats.active > 0 ? 'bg-emerald-500/20 text-emerald-400' : 'bg-zinc-700 text-muted-foreground'}`}>
                          {stats.active > 0 ? 'ON ROAD' : driverTab === 'inactive' ? 'INACTIVE' : 'IDLE'}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-foreground font-mono">{stats.active}</TableCell>
                    <TableCell className="text-xs text-muted-foreground font-mono">{stats.completed}</TableCell>
                    <TableCell className="text-xs text-muted-foreground font-mono">{stats.totalKm.toLocaleString()}</TableCell>
                    <TableCell className="text-xs text-brand-accent font-mono">${stats.earnings.toLocaleString()}</TableCell>
                    <TableCell><ChevronRight className="w-3.5 h-3.5 text-muted-foreground" /></TableCell>
                  </TableRow>
                );
              })}
              {currentList.length === 0 && (
                <TableRow className="border-border"><TableCell colSpan={7} className="text-center text-muted-foreground text-sm py-8">No drivers in this category</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

    </div>
  );
}

function DriverDetail({ driver, loads, token }) {
  const [compliance, setCompliance] = useState({ docs: {}, gps: [], hos: null });

  useEffect(() => {
        Promise.all([
      api.get('/admin/drivers-compliance').catch(() => ({ data: [] })),
      api.get('/tracking/compliance').catch(() => ({ data: [] })),
    ]).then(([compRes, gpsRes]) => {
      const drvComp = (compRes.data || []).find(d => d.user_id === driver.user_id);
      const gpsEvents = (gpsRes.data || []).filter(g => g.driver_id === driver.user_id);
      setCompliance({ docs: drvComp?.documents || {}, gps: gpsEvents, hos: drvComp });
    });
  }, [driver.user_id, token]);

  const driverLoads = loads.filter(l => l.driver_id === driver.user_id);
  const completed = driverLoads.filter(l => ['completed', 'invoiced'].includes(l.status));
  const active = driverLoads.filter(l => ['assigned', 'at_pickup', 'in_transit', 'at_delivery'].includes(l.status));

  return (
    <div className="grid grid-cols-3 gap-4">
      {/* Column 1: Details */}
      <div className="space-y-3">
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-brand-accent/20 flex items-center justify-center text-2xl font-bold text-brand-accent mx-auto">{driver.name?.charAt(0)}</div>
          <h3 className="text-lg font-bold mt-2">{driver.name}</h3>
          <p className="text-xs text-muted-foreground">{driver.email}</p>
          {driver.phone && <p className="text-xs text-muted-foreground">{driver.phone}</p>}
        </div>
        <div className="bg-muted/50 rounded-lg p-3 space-y-2">
          <p className="text-[10px] text-muted-foreground tracking-wider font-bold">DETAILS</p>
          {driver.truck_type && <DetailRow label="Truck" value={driver.truck_type} />}
          {driver.license_number && <DetailRow label="CDL" value={driver.license_number} />}
          <DetailRow label="Status" value={driver.onboarding_completed ? 'Active' : 'Onboarding'} />
          <DetailRow label="Profile" value={`${driver.profile_completion_pct || 0}%`} />
        </div>
        {driver.emergency_contact_name && (
          <div className="bg-muted/50 rounded-lg p-3 space-y-1">
            <p className="text-[10px] text-muted-foreground tracking-wider font-bold">EMERGENCY CONTACT</p>
            <p className="text-xs text-foreground">{driver.emergency_contact_name}</p>
            {driver.emergency_contact_phone && <p className="text-xs text-muted-foreground">{driver.emergency_contact_phone}</p>}
            {driver.emergency_contact_relation && <p className="text-xs text-muted-foreground">{driver.emergency_contact_relation}</p>}
          </div>
        )}
        <div className="bg-muted/50 rounded-lg p-3 space-y-2">
          <p className="text-[10px] text-muted-foreground tracking-wider font-bold">COMPLIANCE</p>
          <DetailRow label="Score" value={compliance.hos?.compliance_score != null ? `${compliance.hos.compliance_score}%` : 'N/A'} />
          <DetailRow label="Docs" value={`${compliance.docs.approved || 0} approved, ${compliance.docs.expired || 0} expired`} />
          <DetailRow label="GPS Events" value={`${compliance.gps.length}`} />
        </div>
      </div>

      {/* Column 2: Activity */}
      <div className="space-y-3">
        <p className="text-[10px] text-muted-foreground tracking-wider font-bold">ACTIVE LOADS ({active.length})</p>
        {active.length > 0 ? active.map(ld => (
          <div key={ld.load_id} className="bg-muted/50 rounded-lg p-3">
            <div className="flex items-center justify-between mb-1">
              <p className="text-xs text-foreground font-medium truncate">{ld.cargo_description}</p>
              <Badge className={`text-[10px] ${getLoadStatusClasses(ld.status)}`}>{ld.status?.replace(/_/g, ' ').toUpperCase()}</Badge>
            </div>
            <p className="text-[10px] text-muted-foreground">{(ld.pickup_name || ld.pickup_address)?.split(',')[0]} → {(ld.delivery_name || ld.delivery_address)?.split(',')[0]}</p>
            <p className="text-[10px] text-muted-foreground mt-1">{milesToKm(ld.distance_miles)} km | ${ld.rate?.toLocaleString()}</p>
          </div>
        )) : <p className="text-xs text-muted-foreground py-4 text-center">No active loads</p>}

        {compliance.gps.length > 0 && (
          <>
            <p className="text-[10px] text-muted-foreground tracking-wider font-bold mt-4">GPS EVENTS</p>
            {compliance.gps.slice(0, 5).map((g, i) => (
              <div key={i} className="bg-muted/50 rounded-lg p-2">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-3 h-3 text-amber-400 flex-shrink-0" />
                  <span className="text-[10px] text-muted-foreground">{g.event_type?.replace(/_/g, ' ')}</span>
                  <span className="text-[10px] text-muted-foreground ml-auto">{g.gap_duration_seconds ? `${Math.round(g.gap_duration_seconds / 60)}m` : ''}</span>
                </div>
                {g.resolution && <p className="text-[10px] text-muted-foreground mt-1 ml-5">{g.resolution}</p>}
              </div>
            ))}
          </>
        )}
      </div>

      {/* Column 3: Load History */}
      <div className="space-y-3">
        <p className="text-[10px] text-muted-foreground tracking-wider font-bold">LOAD HISTORY ({completed.length})</p>
        <div className="grid grid-cols-2 gap-2 mb-2">
          <div className="bg-muted/50 rounded-lg p-2 text-center">
            <p className="text-sm font-bold text-brand-accent">${completed.reduce((s, l) => s + (l.rate || 0), 0).toLocaleString()}</p>
            <p className="text-[10px] text-muted-foreground">EARNED</p>
          </div>
          <div className="bg-muted/50 rounded-lg p-2 text-center">
            <p className="text-sm font-bold text-foreground">{completed.reduce((s, l) => s + milesToKm(l.distance_miles), 0).toLocaleString()} km</p>
            <p className="text-[10px] text-muted-foreground">DRIVEN</p>
          </div>
        </div>
        <div className="max-h-[400px] overflow-y-auto space-y-2">
          {completed.length > 0 ? completed.map(ld => (
            <div key={ld.load_id} className="bg-muted/50 rounded-lg p-3">
              <p className="text-xs text-foreground font-medium truncate">{ld.cargo_description}</p>
              <p className="text-[10px] text-muted-foreground mt-1">{(ld.pickup_name || ld.pickup_address)?.split(',')[0]} → {(ld.delivery_name || ld.delivery_address)?.split(',')[0]}</p>
              <div className="flex items-center justify-between mt-1">
                <span className="text-[10px] text-muted-foreground">{milesToKm(ld.distance_miles)} km</span>
                <span className="text-[10px] text-brand-accent font-mono">${ld.rate?.toLocaleString()}</span>
              </div>
            </div>
          )) : <p className="text-xs text-muted-foreground py-4 text-center">No completed loads</p>}
        </div>
      </div>
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-[10px] text-muted-foreground">{label}</span>
      <span className="text-xs text-foreground">{value}</span>
    </div>
  );
}

function CreateDriverDialog({ open, onOpenChange, token, onCreated }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '', password: '' });
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleCreate = async () => {
    if (!form.name || !form.email) { toast.error('Name and email required'); return; }
    if (!form.password || form.password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setSaving(true);
    try {
      await api.post('/users', { ...form, role: 'driver' });
      toast.success('Driver created! Invite email sent.');
      onOpenChange(false);
      setForm({ name: '', email: '', phone: '', password: '' });
      onCreated();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button className="bg-brand-accent hover:bg-yellow-500 text-brand-accent-foreground font-bold rounded-lg text-xs tracking-wide h-9 px-4">
          <Plus className="w-4 h-4 mr-1" /> ADD DRIVER
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-surface border-border text-foreground max-w-sm">
        <DialogHeader><DialogTitle className="tracking-wider">Add New Driver</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground -mt-2">Driver will receive an invite to download the app and complete onboarding.</p>
        <div className="space-y-3 mt-2">
          <div><Label className="text-muted-foreground text-xs">Name</Label>
            <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="bg-muted border-input text-foreground rounded-lg mt-1" placeholder="Full name" /></div>
          <div><Label className="text-muted-foreground text-xs">Email</Label>
            <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="bg-muted border-input text-foreground rounded-lg mt-1" placeholder="driver@email.com" /></div>
          <div><Label className="text-muted-foreground text-xs">Phone</Label>
            <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="bg-muted border-input text-foreground rounded-lg mt-1" placeholder="416-555-0100" /></div>
          <div><Label className="text-muted-foreground text-xs">Password</Label>
            <div className="flex gap-2 mt-1">
              <div className="relative flex-1">
                <Input type={showPassword ? 'text' : 'password'} value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} className="bg-muted border-input text-foreground rounded-lg pr-9" placeholder="Min 6 characters" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <Button type="button" variant="outline" className="border-input text-muted-foreground text-xs px-3 shrink-0" onClick={() => setForm({ ...form, password: Math.random().toString(36).slice(2, 12) })}>Generate</Button>
            </div>
          </div>
        </div>
        <Button onClick={handleCreate} disabled={saving} className="w-full bg-brand-accent hover:bg-yellow-500 text-brand-accent-foreground font-bold rounded-lg mt-4 tracking-wider">
          {saving ? 'SENDING INVITE...' : 'SEND INVITE'}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
